import { FunctionComponent, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Navigate, useSearchParams } from "react-router-dom";

import { PageHeader } from "@/components";
import { TabPanel } from "@/components/Tabs/Tabs";

import { useAppSelector } from "@/redux/hooks";
import { authSelector } from "@/pages/Public/redux/auth.slice";

import { PermissionRoles } from "@/enum";

import { ROUTE_CONFIG } from "@/routes/config";

import { useHasPermissions } from "@/utils/useHasPermissions";

import { CompanyInformation } from "./components/CompanyInformation";
import { ContactSettings } from "./components/ContactSettings";
import { PositiveReplyTrackingSettings } from "./components/PositiveReplyTrackingSettings";
import { SearchDefaultsSettings } from "./components/SearchDefaultsSettings";
import { CompanyType } from "./schema/company";
import { Users } from "../Users/Users";
import { Usage } from "../Usage/Usage";

enum Tabs {
	GENERAL = "GENERAL",
	USER = "USER",
	CONTACTS = "CONTACTS",
	BILLING = "BILLING",
}

// Legacy tab values that now live on the Integrations surface (Integrations catalog, and the
// API keys / webhooks that moved out of the former Developer tab). Old links redirect there.
const REDIRECT_TO_INTEGRATIONS_TABS = new Set(["INTEGRATIONS", "DEVELOPER"]);
// Legacy tabs that have moved elsewhere (Copywriting → own nav item, Blacklist → Contacts,
// Costs removed). Old links land on General.
const REMOVED_TABS = new Set(["COSTS", "BLACKLIST", "COPYWRITING"]);

const resolveTab = (raw: string | null): Tabs => {
	if (!raw || REMOVED_TABS.has(raw) || !(raw in Tabs)) {
		return Tabs.GENERAL;
	}

	return raw as Tabs;
};

export const Company: FunctionComponent = () => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`company.${key}`);
	const [searchParams, setSearchParams] = useSearchParams();

	const requestedTab = searchParams.get("tab");
	const shouldRedirectToIntegrations = REDIRECT_TO_INTEGRATIONS_TABS.has(requestedTab ?? "");

	const [currentTab, setCurrentTab] = useState<Tabs>(resolveTab(requestedTab));

	const { user } = useAppSelector(authSelector);

	const { hasPermissions } = useHasPermissions();

	useEffect(() => {
		if (searchParams.has("tab")) {
			setCurrentTab(resolveTab(searchParams.get("tab")));
		}
	}, [searchParams]);

	useEffect(() => {
		if (shouldRedirectToIntegrations) {
			return;
		}

		const nextTab = resolveTab(searchParams.has("tab") ? searchParams.get("tab") : null);

		if (nextTab !== currentTab || searchParams.get("tab") !== nextTab) {
			setSearchParams({ ...searchParams, tab: nextTab }, { replace: true });
		}
	}, [searchParams]);

	const handleTabsChange = useCallback(
		(value: string): void => {
			setSearchParams(
				(prev) => {
					const next = new URLSearchParams(prev);

					next.set("tab", value);
					// The former Developer tab owned the subTab param; no current tab uses it.
					next.delete("subTab");

					return next;
				},
				{ replace: true }
			);
		},
		[setSearchParams]
	);

	if (shouldRedirectToIntegrations) {
		return <Navigate replace to={ROUTE_CONFIG.INTEGRATIONS} />;
	}

	let tabs = [
		{ label: ts("tabs.general"), value: Tabs.GENERAL, permission: PermissionRoles.COMPANY },
		{ label: ts("tabs.user"), value: Tabs.USER, permission: PermissionRoles.COMPANY },
		{ label: ts("tabs.contacts"), value: Tabs.CONTACTS, permission: PermissionRoles.COMPANY },
		{ label: ts("tabs.billing"), value: Tabs.BILLING, permission: PermissionRoles.COMPANY },
	];

	tabs = tabs.filter((tab) => hasPermissions([tab.permission]));

	return (
		<div className="flex flex-col w-full">
			<PageHeader
				activeTab={currentTab}
				tabs={tabs.map((tab) => ({ label: tab.label, value: tab.value }))}
				title={ts("heading")}
				onTabChange={handleTabsChange}
			/>

			<TabPanel activeTab={currentTab} value={Tabs.GENERAL}>
				<div className="flex flex-col gap-4">
					<CompanyInformation company={user?.company as CompanyType} />
					<PositiveReplyTrackingSettings />
					<SearchDefaultsSettings />
				</div>
			</TabPanel>

			<TabPanel activeTab={currentTab} value={Tabs.USER}>
				<Users />
			</TabPanel>

			<TabPanel activeTab={currentTab} value={Tabs.CONTACTS}>
				<ContactSettings />
			</TabPanel>

			<TabPanel activeTab={currentTab} value={Tabs.BILLING}>
				<Usage />
			</TabPanel>
		</div>
	);
};
